import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Numeral from 'numeral';

import FormUtils from '../utils/Form';

import { BorderRadius, Colors, FontSizes, FontWeights, FontFamilies } from '../Theme';

const Input = ({
  format,
  height,
  invalid,
  max,
  maxLength,
  min,
  onBlur,
  onChange,
  onKeyDown,
  placeholder,
  readOnly,
  required,
  step,
  style,
  type = 'text',
  value
}) => {
  const [inputFocus, setInputFocus] = useState(false);
  const unformatted_value = value;
  const focus_color = inputFocus ? '#aaaaaa' : '#ccc';
  const border_color = invalid ? Colors.RED.hex : focus_color;
  const styles = {
    component: Object.assign(
      {},
      {
        backgroundColor: readOnly ? '#f5f5f5' : '#fff',
        borderRadius: BorderRadius.DEFAULT,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: border_color,
        fontSize: FontSizes.MEDIUM,
        position: 'relative',
        height: height || 40,
        lineHeight: '20px',
        padding: 10
      },
      style
    ),
    input: {
      fontSize: FontSizes.MEDIUM,
      fontWeight: FontWeights.REGULAR,
      fontFamily: FontFamilies.MAIN,
      lineHeight: '20px',
      backgroundColor: 'transparent',
      border: 'none',
      color: readOnly ? '#777' : Colors.GRAY.hex,
      boxShadow: 'inset 0 0 100px 100px transparent',
      padding: 0,
      margin: 0,
      textIndent: 0,
      outline: 'none',
      width: '100%',
      height: height ? (height || 100) - 20 : 20,
      fontStyle: readOnly ? 'italic' : 'normal',
      WebkitAppearance: 'none'
    },
    required: {
      color: Colors.RED.hex,
      position: 'absolute',
      right: 5,
      top: 5,
      lineHeight: '1',
      fontSize: FontSizes.LARGE
    }
  };

  const handleBlur = e => {
    setInputFocus(false);

    if (onBlur) onBlur(e);
  };

  let formatted_value = value;

  if (format === 'phone') {
    formatted_value = FormUtils._formatAsPhone(unformatted_value);
  }

  if (format === 'currency') {
    if (`${unformatted_value}`.length) {
      formatted_value = Numeral(unformatted_value).format('$0,0');
    }
  }

  if (format === 'currency_decimal') {
    formatted_value = `$${unformatted_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  }

  if (format === 'ssn' && type !== 'password') {
    formatted_value = FormUtils._formatAsSsn(unformatted_value);
  }

  const handleChange = e => {
    if (format === 'currency') {
      e.target.value = e.target.value.replace(/[^0-9.]/g, '');
    }

    if (format === 'currency_decimal') {
      e.target.value = e.target.value
        .replace(/(?!\.)\D/g, '')
        .replace(/(?<=\..*)\./g, '')
        .replace(/(?<=\.\d\d).*/g, '')
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    if (format === 'phone') {
      e.target.value = e.target.value.replace(/[^0-9.]/g, '').substring(0, 10);
    }

    if (format === 'ssn') {
      e.target.value = e.target.value.replace(/[^0-9]/g, '').substring(0, 9);
    }

    if (format === 'zip') {
      e.target.value = e.target.value.replace(/[^0-9]/g, '').substring(0, 5);
    }

    if (type === 'email') {
      e.target.value = e.target.value.toLowerCase();
    }

    if (type === 'number' && max) {
      e.target.value = e.target.value > max ? max : e.target.value;
    }

    if (!readOnly && onChange) onChange(e);
  };

  return (
    <div style={styles.component}>
      {type === 'textarea' ? (
        <textarea
          maxLength={maxLength}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={readOnly ? null : setInputFocus.bind(null, true)}
          placeholder={placeholder}
          readOnly={readOnly}
          style={styles.input}
          type={type}
          value={formatted_value}
        />
      ) : (
        <input
          max={max}
          maxLength={maxLength}
          min={min}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={readOnly ? null : setInputFocus.bind(null, true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          readOnly={readOnly}
          step={step}
          style={styles.input}
          type={type}
          value={formatted_value}
        />
      )}
      {required ? <span style={styles.required}>*</span> : null}
    </div>
  );
};

Input.propTypes = {
  format: PropTypes.oneOf(['phone', 'currency', 'currency_decimal', 'ssn', 'zip']),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  invalid: PropTypes.bool,
  max: PropTypes.number,
  maxLength: PropTypes.number,
  min: PropTypes.number,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
  type: PropTypes.oneOf(['text', 'number', 'password', 'phone', 'email', 'textarea']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default Input;
